/** Rich-text email body for every mailbox (Emma, 9 Oct 2026): bold / italic / underline / bullets / numbering / links.
 *  Emits both HTML (what Outlook sends) and plain text (what we log on files). Ctrl+B / I / U work as in Outlook. */
import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { DOMParser as PMDOMParser } from "@tiptap/pm/model";
import { Bold, Italic, Link2, List, ListOrdered, RemoveFormatting, Underline } from "lucide-react";

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
/** Plain text (AI drafts, prefills, templates) → paragraphs the editor understands. */
export const textToEditorHtml = (text) => (text || "").replace(/\r\n/g, "\n").split("\n").map((l) => (l.trim() ? `<p>${esc(l)}</p>` : "<p></p>")).join("");

/** Pasted plain text keeps EVERY blank line as an empty paragraph (ProseMirror's default collapses "\n\n" into a bare
 *  paragraph break, so pasted emails lost their spacing — Juffrey, 12 Oct 2026). */
const pasteTextKeepingBlankLines = (text, $context, _plain, view) => {
  const dom = document.createElement("div");
  dom.innerHTML = textToEditorHtml(text);
  return PMDOMParser.fromSchema(view.state.schema).parseSlice(dom, { preserveWhitespace: true, context: $context });
};

function ToolButton({ on, active, title, children, testId }) {
  return (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); on(); }} title={title} data-testid={testId} aria-pressed={!!active}
      className={`h-7 w-7 inline-flex items-center justify-center rounded text-slate-600 hover:bg-slate-200/70 ${active ? "bg-slate-200 text-slate-900" : ""}`}>
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = 240, autoFocus = false, testId = "rich-editor", disabled = false }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false, codeBlock: false, code: false, blockquote: false, horizontalRule: false, link: { openOnClick: false, autolink: true, defaultProtocol: "https" } })],
    content: value || "",
    autofocus: autoFocus ? "end" : false,
    editable: !disabled,
    immediatelyRender: true,
    editorProps: { clipboardTextParser: pasteTextKeepingBlankLines, attributes: { class: "outline-none text-sm leading-relaxed px-3 py-2 prose-sm max-w-none [&_p]:my-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-700 [&_a]:underline", "data-testid": `${testId}-content`, "data-placeholder": placeholder || "" } },
    onUpdate: ({ editor: ed }) => onChange({ html: ed.isEmpty ? "" : ed.getHTML(), text: ed.getText({ blockSeparator: "\n" }) }),
  });

  // external replace (an AI draft arriving, a template inserted) — only when it differs from what the editor already
  // holds; emits onChange so the caller's plain-text copy follows the new content
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML() && !(value === "" && editor.isEmpty)) editor.commands.setContent(value || "", { emitUpdate: true });
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link address", prev || "https://");
    if (url === null) return;
    if (!url.trim()) { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  const empty = editor.isEmpty;
  return (
    <div className={`rounded-md border border-input bg-white focus-within:ring-1 focus-within:ring-ring ${disabled ? "opacity-60" : ""}`} data-testid={testId}>
      <div className="flex items-center gap-0.5 border-b bg-slate-50/80 px-1.5 py-1" data-testid={`${testId}-toolbar`}>
        <ToolButton on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Ctrl+B)" testId={`${testId}-bold`}><Bold className="w-3.5 h-3.5" /></ToolButton>
        <ToolButton on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Ctrl+I)" testId={`${testId}-italic`}><Italic className="w-3.5 h-3.5" /></ToolButton>
        <ToolButton on={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline (Ctrl+U)" testId={`${testId}-underline`}><Underline className="w-3.5 h-3.5" /></ToolButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolButton on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId={`${testId}-bullets`}><List className="w-3.5 h-3.5" /></ToolButton>
        <ToolButton on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId={`${testId}-numbers`}><ListOrdered className="w-3.5 h-3.5" /></ToolButton>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <ToolButton on={setLink} active={editor.isActive("link")} title="Insert link" testId={`${testId}-link`}><Link2 className="w-3.5 h-3.5" /></ToolButton>
        <ToolButton on={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testId={`${testId}-clear`}><RemoveFormatting className="w-3.5 h-3.5" /></ToolButton>
      </div>
      <div className="relative" style={{ minHeight }}>
        {empty && placeholder && <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground" data-testid={`${testId}-placeholder`}>{placeholder}</div>}
        <EditorContent editor={editor} style={{ minHeight }} />
      </div>
    </div>
  );
}
